// src/components/SecretAdmirerBadge.tsx
// Brief W: participation gate — hide the count until the user has added at
// least one crush. Show a prompt card with "Add a Crush" CTA before that.
// Cap displayed count at "99+". Fire-and-forget tally backfill once per session.

import { useState, useEffect, useRef } from 'react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';

const client = generateClient();

const Q_GET_TALLY = /* GraphQL */ `
  query GetSecretAdmirerTally($userId: String!) {
    getSecretAdmirerTally(userId: $userId) {
      userId
      allTimeAdds
      unreadAdds
      todayAdds
    }
  }
`;

interface SecretAdmirerBadgeProps {
  userId: string;
  className?: string;
  isPaid?: boolean;
  onUpgrade?: () => void;
  onTallyLoaded?: (count: number) => void;
  totalCrushesAdded?: number | null;
  onAddCrush?: () => void;
}

export function SecretAdmirerBadge({
  userId,
  className = '',
  isPaid = false,
  onUpgrade,
  onTallyLoaded,
  totalCrushesAdded,
  onAddCrush,
}: SecretAdmirerBadgeProps) {
  const [allTimeTotal, setAllTimeTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const handler = () => setRefreshKey(k => k + 1);
    window.addEventListener('crushhh:contactConfirmed', handler);
    return () => window.removeEventListener('crushhh:contactConfirmed', handler);
  }, []);

  // Session-scoped guard — fire the SecretAdmirerTally backfill mutation once
  // when the gate first unlocks. Per Brief W / V the mutation is idempotent
  // server-side via ConditionExpression, but we still avoid spamming it.
  const hasBackfilled = useRef(false);

  const gateUnlocked = !!totalCrushesAdded && totalCrushesAdded > 0;

  // Backfill trigger — fires on the first render where the gate is unlocked.
  // After the mutation resolves, bump refreshKey to re-fetch the tally so the
  // seeded count is displayed without requiring a re-login.
  useEffect(() => {
    if (gateUnlocked && !hasBackfilled.current) {
      hasBackfilled.current = true;
      void (async () => {
        try {
          await (client.graphql({
            query: /* GraphQL */ `mutation InitializeTally { initializeSecretAdmirerTally }`,
            authMode: 'userPool',
          }) as Promise<unknown>);
          setRefreshKey(k => k + 1);
        } catch { /* fire-and-forget */ }
      })();
    }
  }, [gateUnlocked]);

  useEffect(() => {
    // Don't bother fetching the tally while the gate is closed — the user
    // never sees the count anyway and the row likely doesn't exist yet.
    if (!gateUnlocked) {
      setLoading(false);
      onTallyLoaded?.(0);
      return;
    }

    let mounted = true;

    const loadData = async () => {
      try {
        const tallyResult = await client.graphql({
          query: Q_GET_TALLY,
          variables: { userId },
          authMode: 'userPool',
        }) as GraphQLResult<{
          getSecretAdmirerTally: {
            userId: string;
            allTimeAdds: number;
            unreadAdds: number;
            todayAdds: number;
          } | null;
        }>;

        if (!mounted) return;

        const tally = tallyResult.data?.getSecretAdmirerTally;
        const count = tally?.allTimeAdds ?? 0;
        setAllTimeTotal(count);
        setLoading(false);
        onTallyLoaded?.(count);
      } catch {
        if (mounted) {
          setAllTimeTotal(0);
          setLoading(false);
          onTallyLoaded?.(0);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [userId, gateUnlocked, refreshKey]);

  // Gate locked: show the participation prompt instead of any count.
  // Do not reveal the existence of admirers in any way.
  if (!gateUnlocked) {
    return (
      <div className={`flex flex-col gap-3 mb-3 ${className}`}>
        <div
          className="bg-white border-4 border-branddark rounded-lg p-5"
          style={{ boxShadow: "4px 4px 0px #E05A3A" }}
        >
          <h3
            style={{
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "#1A1A1A",
              margin: 0,
              marginBottom: 8,
              lineHeight: 1.15,
            }}
          >
            Is someone waiting for you?
          </h3>
          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 15,
              color: "#555",
              margin: 0,
              marginBottom: 16,
              lineHeight: 1.4,
            }}
          >
            Add your first crush to find out.
          </p>
          {onAddCrush && (
            <button
              onClick={onAddCrush}
              style={{
                background: "#E05A3A",
                color: "#FFFFFF",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 18,
                fontWeight: 700,
                border: "3px solid #1A1A1A",
                borderRadius: 100,
                boxShadow: "4px 4px 0px #1A1A1A",
                padding: "12px 28px",
                cursor: "pointer",
                width: "100%",
                boxSizing: "border-box",
                transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
              }}
            >
              Add a Crush
            </button>
          )}
        </div>
      </div>
    );
  }

  // Gate unlocked. Loading state — render nothing rather than flash.
  if (loading) return null;

  const displayCount = allTimeTotal >= 100 ? '99+' : String(allTimeTotal);
  const showEmptyState = allTimeTotal === 0;

  return (
    <div className={`flex flex-row gap-4 sm:gap-6 mb-3 ${className}`}>
      {/* Upgrade Button (only for free users with admirers) */}
      {!isPaid && onUpgrade && !showEmptyState && (
        <button
          onClick={onUpgrade}
          style={{
            background: "#E05A3A",
            color: "#FFFFFF",
            fontFamily: "'Fredoka', sans-serif",
            fontSize: 24,
            fontWeight: 700,
            border: "3px solid #1A1A1A",
            borderRadius: 16,
            boxShadow: "4px 4px 0px #1A1A1A",
            padding: "16px 28px",
            lineHeight: 1.2,
            textAlign: "center",
            cursor: "pointer",
            flexShrink: 0,
            display: "block",
            width: "100%",
            maxWidth: 160,
          }}
        >
          Unlock
          <br />
          Matching
        </button>
      )}

      {/* Secret Crush Badge */}
      <div
        className="bg-white border-4 border-branddark rounded-lg shadow-md p-5 flex-1"
        style={{ boxShadow: "4px 4px 0px #E05A3A" }}
      >
        {showEmptyState ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 60,
                fontWeight: 700,
                color: "#E05A3A",
                lineHeight: 1,
              }}
            >
              0
            </div>
            <div
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 15,
                color: "#555",
                lineHeight: 1.4,
              }}
            >
              No secret crushes yet — keep sharing!
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 80,
                fontWeight: 700,
                color: "#E05A3A",
                lineHeight: 1,
              }}
            >
              {displayCount}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <span
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 32,
                  fontWeight: 700,
                  color: "#1A1A1A",
                  lineHeight: 1.1,
                }}
              >
                Secret
              </span>
              <span
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 32,
                  fontWeight: 700,
                  color: "#1A1A1A",
                  lineHeight: 1.1,
                }}
              >
                {allTimeTotal === 1 ? "Crush" : "Crushes"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
